import type { FlagKind } from 'definitions';
import type { CreateFlagInput, FlagsListManager } from './useFlags';
import { useState } from 'react';
import { FLAG_KEY_PATTERN } from 'definitions';
import { suggestFlagKey } from './flag-drafts';

type PayloadCheck = { ok: true; value: unknown } | { ok: false };

/** A payload as typed: empty is none, anything else must parse as JSON. */
export function parsePayload(text: string): PayloadCheck {
  if (!text.trim()) return { ok: true, value: undefined };
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
}

type BlockingParams = {
  name: string;
  flagKey: string;
  offPayload: PayloadCheck;
  onPayload: PayloadCheck;
};

/** What still stops the flag being created, as a sentence; undefined when nothing does. */
export function createBlockingReason({ name, flagKey, offPayload, onPayload }: BlockingParams): string | undefined {
  if (!name.trim()) return 'Give the flag a name';
  if (!flagKey) return 'Give the flag a key';
  if (!FLAG_KEY_PATTERN.test(flagKey)) return 'Use lowercase letters, digits and hyphens in the key';
  if (!offPayload.ok) return 'The Off payload is not valid JSON';
  if (!onPayload.ok) return 'The On payload is not valid JSON';
  return undefined;
}

/**
 * The Create flag dialog. The key follows the name until it is typed in by hand. The description
 * is the Brief's Description & notes. Bucket on and
 * the payloads are for code flags only: an editor flag is decided in the browser, which has
 * no customer ID, and its On side is the editor's changes.
 */
export const useCreateFlag = (list: FlagsListManager) => {
  const [name, setNameValue] = useState('');
  const [keyDraft, setKeyDraft] = useState<string | null>(null);
  const [kind, setKind] = useState<FlagKind>('editor');
  const [bucketOn, setBucketOn] = useState<CreateFlagInput['bucketOn']>('visitor');
  const [description, setDescription] = useState('');
  const [offPayloadText, setOffPayloadText] = useState('');
  const [onPayloadText, setOnPayloadText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const flagKey = (keyDraft ?? suggestFlagKey(name)).trim();
  const keyError = flagKey && !FLAG_KEY_PATTERN.test(flagKey);
  const isCode = kind === 'code';
  const offPayload = isCode ? parsePayload(offPayloadText) : { ok: true as const, value: undefined };
  const onPayload = isCode ? parsePayload(onPayloadText) : { ok: true as const, value: undefined };
  const blockingReason = createBlockingReason({ name, flagKey, offPayload, onPayload });

  const submit = async () => {
    if (blockingReason || !offPayload.ok || !onPayload.ok) return null;
    setIsSubmitting(true);
    try {
      const input: CreateFlagInput = {
        name: name.trim(),
        // The Brief's Description & notes: the same column, so what is typed here starts it.
        description: description.trim(),
        flagKey,
        kind,
        bucketOn: isCode ? bucketOn : 'visitor',
        ...(offPayload.value === undefined ? {} : { offPayload: offPayload.value }),
        ...(onPayload.value === undefined ? {} : { onPayload: onPayload.value }),
      };
      return await list.createFlag(input);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    name,
    setName: setNameValue,
    flagKey,
    setFlagKey: (value: string) => setKeyDraft(value),
    keyError: !!keyError,
    kind,
    setKind,
    isCode,
    bucketOn,
    setBucketOn,
    description,
    setDescription,
    offPayloadText,
    setOffPayloadText,
    onPayloadText,
    setOnPayloadText,
    offPayloadInvalid: !offPayload.ok,
    onPayloadInvalid: !onPayload.ok,
    blockingReason,
    isSubmitting,
    submit,
  };
};

export type CreateFlagManager = ReturnType<typeof useCreateFlag>;
